/**
 * url helper module.
 * @module helpers/url
 */


/**
 * concatenate an array of params objects to a list of querystring partials.
 * @param {Array} params The array of parameters. Ex: [{name:'foo', value:'bar'}, {name:'funder', value:'123123'}]
 * @returns {Array} Querystring generated. Ex: ['foo=bar', 'funder=123123']
 */
export function paramsToQS(params) {
    return params.map(({ name, value }) => `${name}=${value}`);
}

/**
 * concatenate a URL based on a host and an array of paths
 * @param {string} host The host like: http://www.whatever.com
 * @param {string|array} paths The additional paths, can be an array of "subplace" strings
 * @returns {string} Url composed by host + paths
 */
export function concatUrl(host, ...paths) {
    const paths_string = paths.
        map((item) => item.replace(/\//g, '')).
        join('/');

    if (paths.length === 0 || paths_string === '') {
        return host;
    }
    return `${host}${host.substring(host.length - 1) === '/' ? '' : '/'}${paths_string}`;
}

/**
 * concatenate a URL (which can contains already some parameters) with an additional querystring
 * @param {string} url The base url Ex: /discover or /discover?foo=bar
 * @param {string|array} params The additional querystring, can be an array of "key=value" strings
 * @returns {string} Url composed by url + querystring
 */
export function concatUrlByParams(url, ...params) {
    const qs_params = params.
        filter((item) => (Array.isArray(item) ? item.length > 0 : true)).
        map((item) => (Array.isArray(item) ? item.join('&') : item)).
        join('&').
        replace(/^\?/, '');

    if (params.length === 0 || qs_params === '') {
        return url;
    }
    return `${url}${url.indexOf('?') >= 0 ? '&' : '?'}${qs_params}`;
}

/**
 * removes a param from a url.
 */
export function removeParam(url = '', paramName) {
    const sep = url.indexOf('?');
    const path = sep !== -1 ? url.slice(0, sep) : url;
    const params = sep !== -1 ? url.slice(sep + 1) : '';
    const filtered = params.split('&').filter((param) => param.split('=')[0] !== paramName);

    return concatUrl(path, filtered);
}

/**
 * adds or replaces a param / value pair from a url.
 * @param {string} url
 * @param {string} name
 * @param {string} [null] value
 * @param {boolean} overwrite
 */
export function addParam(url, name, value, overwrite = false) {
    const qs = overwrite ? removeParam(url, name) : url;
    return concatUrl(qs, `${name}${value !== undefined ? `=${value}` : ''}`);
}

/**
 * adds or replaces a param / value group from a url.
 * @param {string} url
 * @param {object} params a key/value object of params to be added / replaced
 * @param {boolean} overwrite
 */
export function addParams(url, params, overwrite) {
    return Object.keys(params).
        map((key) => ({ key, value: params[key] })).
        reduce((acc, { key, value }) => addParam(acc, key, value, overwrite), url);
}

/**
 * gets the named param from the querystring
 * @param {string} querystring.
 * @param {string} paramName
 * @returns {string} The param value or null
 */
export function getParam(querystring = '', paramName) {
    // cleanup: removes the hash part and the initial question mark - if present
    const clean = querystring.replace(/^\?/, '').split('#')[0];
    const pair = clean.split('&').find((str) => str.indexOf(`${paramName.toLowerCase()}=`) === 0);

    if (pair) {
        return pair.split('=')[1];
    }
    return null;
}

/**
 * get object with all query params from the querystring
 * @param {string} querystring.
 * @returns {object} All param values
 */
export function getParams(querystring = '') {
    // cleanup: removes the hash part and the initial question mark - if present
    const clean = querystring.replace(/^\?/, '').split('#')[0];
    const pairs = clean.split('&');

    const result = {};

    pairs.forEach((pair) => {
        pair = pair.split('=');
        result[pair[0]] = decodeURIComponent(pair[1] || '');
    });

    return result;
}

/**
 * given a filter key, gets all that filter param/values from the querystring
 * For example from ?and_facet_year=2017&subset_filters=4&subset_foo=bar
 * Looking for `subset` would return [{key: 'filters, value:4}, {key:'foo', value: 'bar'}]
 * @param {string} querystring.
 * @param {string} filterkey
 * @returns {array} The list of keys / values from that filters
 */
export function getFilterValues(querystring = '', filterkey) {
    const regex = new RegExp(`${filterkey}_(\\w+)=([^\\&]+)`, 'gi');
    let match = regex.exec(querystring);

    const r = [];

    while (match !== null) {
        const key = match[1];
        const value = match[2];

        r.push({ key, value });
        match = regex.exec(querystring);
    }

    return r;
}

export default {
    concatUrl,
    concatUrlByParams,
    paramsToQS,
    removeParam,
    addParam,
    getParam,
    getParams
};
